import {useState, useRef, type ReactNode} from 'react';
import {createPortal} from 'react-dom';
import {
  EXPORT_PEEK_PANEL,
  EXPORT_PEEK_WRAPPER,
  useExportTarget,
} from '@/matic/lib/export/export-mode';
import {cn} from '@/matic/lib/utils/cn';

type TooltipProps = {
  readonly content: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  /**
   * - "column": left-aligns with the closest `<th>` ancestor, constrains
   *   width to the column width.
   * - "cell": left-aligns with the closest `<td>` ancestor, constrains
   *   width to the cell width.
   * - "center" (default): centers below the trigger element.
   */
  readonly align?: 'center' | 'column' | 'cell' | 'viewport';
};

/**
 * Visual styling of the tooltip panel, shared by the live (portalled) and the
 * exported (CSS-hover) renderings so an exported peek looks identical.
 */
function panelClassName(align: NonNullable<TooltipProps['align']>): string {
  if (align === 'viewport') {
    return 'rounded-lg bg-white p-5 text-xs text-neutral-700 shadow-[0_2px_6px_rgba(15,23,42,0.06),0_16px_40px_-12px_rgba(15,23,42,0.28)] ring-1 ring-neutral-900/5';
  }

  return `rounded-md border border-neutral-200 bg-white shadow-md ${
    align === 'cell'
      ? 'px-3 py-2 text-sm text-neutral-800'
      : 'px-2.5 py-1.5 text-xs text-neutral-700'
  }`;
}

/**
 * Lightweight styled tooltip — renders via portal so it's never clipped by
 * overflow:hidden parents (e.g. table cells / sticky headers). Under static
 * export it renders the panel inline instead, revealed by CSS on hover.
 */
export function Tooltip({
  content,
  children,
  className,
  align = 'center',
}: TooltipProps) {
  const exportTarget = useExportTarget();
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    maxWidth: 0,
    flipped: false,
  });
  const triggerRef = useRef<HTMLSpanElement>(null);

  const show = () => {
    const el = triggerRef.current;
    if (!el) return;

    if (align === 'column' || align === 'cell') {
      const ancestor = el.closest(
        align === 'column' ? 'th' : 'td'
      ) as HTMLElement | null;
      const anchor = ancestor ?? el;
      const rect = anchor.getBoundingClientRect();
      setCoords({
        // "cell" overlaps the cell itself; "column" drops below the header.
        top: align === 'cell' ? rect.top : rect.bottom + 4,
        left: rect.left,
        maxWidth: Math.max(rect.width, 160),
        flipped: false,
      });
    } else {
      const rect = el.getBoundingClientRect();
      // Flip above trigger if tooltip would overflow viewport bottom
      // (estimate ~80px tooltip height for typical content)
      const spaceBelow = window.innerHeight - rect.bottom;
      const shouldFlip = spaceBelow < 100;
      const preferredWidth = align === 'viewport' ? 320 : 256;
      // `clientWidth` excludes the browser scrollbar. Keep an additional visual
      // gutter so a portal tooltip never looks fused to a RHS scroll track.
      const viewportWidth = document.documentElement.clientWidth;
      const gutter = 24;
      const maxWidth = Math.min(preferredWidth, viewportWidth - gutter * 2);
      const preferredLeft = rect.left + rect.width / 2 - maxWidth / 2;
      const left = Math.min(
        Math.max(preferredLeft, gutter),
        viewportWidth - gutter - maxWidth
      );
      setCoords({
        top: shouldFlip ? rect.top - 6 : rect.bottom + 6,
        left: align === 'viewport' ? left : rect.left + rect.width / 2,
        maxWidth: align === 'viewport' ? maxWidth : 0,
        flipped: shouldFlip,
      });
    }

    setVisible(true);
  };

  const hide = () => {
    setVisible(false);
  };

  // Email: emit no panel at all. A mail client has no reliable hover, and
  // clients that ignore `display:none` would dump every quote panel inline —
  // so the peek has to be absent from the markup, not merely hidden. The
  // trigger stays as the visual anchor; the evidence is one click away in the
  // app or the attached document.
  if (exportTarget === 'email') {
    return <span className={className}>{children}</span>;
  }

  // Static export: no portal (JS never runs in the exported file) and no
  // measured coordinates. The panel ships inline as a sibling of the trigger
  // and is revealed by the CSS in `exportOnlyCss()`.
  if (exportTarget === 'download') {
    return (
      <span className={cn(EXPORT_PEEK_WRAPPER, className)}>
        {children}
        <span
          role="tooltip"
          className={cn(EXPORT_PEEK_PANEL, panelClassName(align))}
        >
          {content}
        </span>
      </span>
    );
  }

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className={className}
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              ...(align === 'column' || align === 'cell' || align === 'viewport'
                ? {maxWidth: coords.maxWidth}
                : {
                    transform: coords.flipped
                      ? 'translate(-50%, -100%)'
                      : 'translateX(-50%)',
                  }),
              zIndex: 9999,
              pointerEvents: 'none',
            }}
            className={panelClassName(align)}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
}
